import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';

interface EditParticipantDialogProps {
  open: boolean;
  onClose: () => void;
  session: any;
  onSuccess: (updatedSession: any) => void;
}

const EditParticipantDialog: React.FC<EditParticipantDialogProps> = ({
  open,
  onClose,
  session,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    participantName: '',
    participantEmail: '',
    participantOrganization: '',
    participantPosition: '',
    participantPhone: '',
    participantAddress: '',
    participantEmployeeCount: '',
  });

  useEffect(() => {
    if (session) {
      setFormData({
        participantName: session.participantName || '',
        participantEmail: session.participantEmail || '',
        participantOrganization: session.participantOrganization || '',
        participantPosition: session.participantPosition || '',
        participantPhone: session.participantPhone || '',
        participantAddress: session.participantAddress || '',
        participantEmployeeCount: session.participantEmployeeCount || '',
      });
    }
  }, [session]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const updateSessionParticipant = httpsCallable(functions, 'updateSessionParticipant');
      const result = await updateSessionParticipant({
        sessionId: session.id,
        ...formData,
      });

      const response = result.data as any;
      if (response.success) {
        toast.success(t['edit.participant.success']);
        onSuccess(response.session);
        onClose();
      }
    } catch (error) {
      console.error('Error updating participant:', error);
      toast.error(t['edit.participant.error']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t['edit.participant.dialog.title']}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t['edit.participant.dialog.name']}
              value={formData.participantName}
              onChange={handleChange('participantName')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t['edit.participant.dialog.email']}
              type="email"
              value={formData.participantEmail}
              onChange={handleChange('participantEmail')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t['edit.participant.dialog.organization']}
              value={formData.participantOrganization}
              onChange={handleChange('participantOrganization')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t['edit.participant.dialog.position']}
              value={formData.participantPosition}
              onChange={handleChange('participantPosition')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t['edit.participant.dialog.phone']}
              value={formData.participantPhone}
              onChange={handleChange('participantPhone')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t['edit.participant.dialog.address']}
              value={formData.participantAddress}
              onChange={handleChange('participantAddress')}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t['edit.participant.dialog.employeeCount']}
              value={formData.participantEmployeeCount}
              onChange={handleChange('participantEmployeeCount')}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t['edit.participant.dialog.cancel']}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t['edit.participant.dialog.updating'] : t['edit.participant.dialog.update']}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditParticipantDialog;